import { useEffect, useMemo, useRef, useState } from "react";
import usePersistStickyFilters from "./usePersistStickyFilters";
import {
  loadRequestListFilterState,
  persistRequestListFilterState,
} from "../utils/requestListStickyFilterPersistence";

/**
 * Shared sticky-filter state for Library Requests / VQQ / VQA list screens.
 * Hydrates from sessionStorage, persists changes, and resets page on
 * filter/search/due-date changes while preserving the hydrated page on mount.
 *
 * @param {{
 *   screenKey: string,
 *   emptySelectedIds: Record<string, unknown[]>,
 *   defaultSortConfig: Record<string, { direction: string }>,
 *   searchQuery?: string,
 *   includeDueDate?: boolean,
 *   useServerSort?: boolean,
 * }} options
 */
export default function useRequestListStickyFilters({
  screenKey,
  emptySelectedIds,
  defaultSortConfig,
  searchQuery = "",
  includeDueDate = false,
  useServerSort = true,
}) {
  const emptySelectedIdsRef = useRef(emptySelectedIds);
  const defaultSortConfigRef = useRef(defaultSortConfig);

  const initialFilterState = useMemo(
    () =>
      loadRequestListFilterState(screenKey, {
        emptySelectedIds: emptySelectedIdsRef.current,
        defaultSortConfig: defaultSortConfigRef.current,
        includeDueDate,
      }),
    [screenKey, includeDueDate]
  );

  const [currentPage, setCurrentPage] = useState(initialFilterState.currentPage);
  const [selectedIds, setSelectedIds] = useState(initialFilterState.selectedIds);
  const [sortConfig, setSortConfig] = useState(initialFilterState.sortConfig);
  const [sortFetchNonce, setSortFetchNonce] = useState(0);
  const [dueDateFilter, setDueDateFilter] = useState(
    () => initialFilterState.dueDateFilter ?? { startDate: "", endDate: "" }
  );

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const newDirection =
        prevConfig[key]?.direction === "asc" ? "desc" : "asc";
      return { [key]: { direction: newDirection } };
    });
    if (useServerSort) {
      setSortFetchNonce((n) => n + 1);
      setCurrentPage(1);
    }
  };

  const persistedFilterState = useMemo(() => {
    const state = {
      selectedIds,
      sortConfig,
      currentPage,
    };
    if (includeDueDate) {
      state.dueDateFilter = dueDateFilter;
    }
    return state;
  }, [
    selectedIds,
    sortConfig,
    currentPage,
    includeDueDate,
    dueDateFilter,
  ]);

  usePersistStickyFilters(
    screenKey,
    persistedFilterState,
    persistRequestListFilterState
  );

  const skipPageResetOnMount = useRef(true);

  useEffect(() => {
    if (skipPageResetOnMount.current) {
      skipPageResetOnMount.current = false;
      return;
    }

    setCurrentPage(1);
  }, [selectedIds, searchQuery, dueDateFilter]);

  return {
    selectedIds,
    setSelectedIds,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    sortFetchNonce,
    dueDateFilter,
    setDueDateFilter,
  };
}
